import React, { useState, useEffect, useCallback } from 'react';
import { rewardAPI, peerRewardAPI, userAPI } from '../../../services';
import { Trophy, Star, IndianRupee, Gift, Send, Users, X } from 'lucide-react';

const CAT_LABELS = { employee_of_month:'Employee of the Month', best_team_player:'Best Team Player', innovation:'Innovation Award', customer_delight:'Customer Delight', leadership:'Leadership Award', above_and_beyond:'Above & Beyond', spot_award:'Spot Award', anniversary:'Work Anniversary', other:'Other' };
const PEER_CAT_LABELS = { teamwork:'Teamwork', innovation:'Innovation', customer_focus:'Customer Focus', leadership:'Leadership', performance:'Performance', other:'Other' };
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const YEAR = new Date().getFullYear();
const RATING_POINTS = { 1:10, 2:20, 3:30, 4:40, 5:50 };
const PERIOD_TYPES = [{ v:'monthly', l:'Monthly' }, { v:'quarterly', l:'Quarterly' }, { v:'yearly', l:'Yearly' }];
const QUARTERS = ['Q1','Q2','Q3','Q4'];
const MONTHS = MONTH_NAMES.map((l, i) => ({ v: i + 1, l }));

const MyRewardsPage = () => {
  const [data, setData] = useState({ rewards: [], totalPoints: 0, totalCash: 0 });
  const [leaderboard, setLeaderboard] = useState([]);
  const [peerData, setPeerData] = useState({ rewards: [], totalPoints: 0, totalCount: 0 });
  const [givenData, setGivenData] = useState({ given: [], givenThisMonth: 0, remainingThisMonth: 5, maxPerMonth: 5 });
  const [peerLeaderboard, setPeerLeaderboard] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState('mine');
  const [lbPeriodType, setLbPeriodType] = useState('monthly');
  const [lbPeriod, setLbPeriod] = useState(String(new Date().getMonth() + 1));
  const [loading, setLoading] = useState(true);
  const [showRewardForm, setShowRewardForm] = useState(false);
  const [rewardForm, setRewardForm] = useState({ receiverId: '', rating: 0, category: 'teamwork', comment: '' });
  const [empSearch, setEmpSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [mine, lb, peerMine, given] = await Promise.all([
        rewardAPI.getMyRewards(),
        rewardAPI.getLeaderboard(YEAR),
        peerRewardAPI.getMyPeerRewards(YEAR),
        peerRewardAPI.getGivenRewards()
      ]);
      setData(mine?.data || { rewards: [], totalPoints: 0, totalCash: 0 });
      setLeaderboard(Array.isArray(lb?.data) ? lb.data : []);
      setPeerData(peerMine?.data || { rewards: [], totalPoints: 0, totalCount: 0 });
      setGivenData(given?.data || { given: [], givenThisMonth: 0, remainingThisMonth: 5, maxPerMonth: 5 });
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const loadPeerLeaderboard = useCallback(async () => {
    try {
      const params = { type: lbPeriodType, year: YEAR };
      if (lbPeriodType === 'monthly') params.period = lbPeriod;
      if (lbPeriodType === 'quarterly') params.period = lbPeriod;
      const res = await peerRewardAPI.getLeaderboard(params);
      setPeerLeaderboard(Array.isArray(res?.data) ? res.data : []);
    } catch { /* ignore */ }
  }, [lbPeriodType, lbPeriod]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'peer-leaderboard') loadPeerLeaderboard(); }, [tab, lbPeriodType, lbPeriod, loadPeerLeaderboard]);

  useEffect(() => {
    if (showRewardForm) {
      userAPI.getUsers({ active: true }).then(r => setEmployees(r?.users || [])).catch(() => {});
    }
  }, [showRewardForm]);

  const handleGivePeerReward = async () => {
    if (!rewardForm.receiverId) return showToast('Select an employee', 'error');
    if (!rewardForm.rating) return showToast('Select a rating', 'error');
    try {
      setSubmitting(true);
      await peerRewardAPI.givePeerReward(rewardForm);
      showToast('Peer reward sent!');
      setShowRewardForm(false);
      setRewardForm({ receiverId: '', rating: 0, category: 'teamwork', comment: '' });
      load();
    } catch (err) { showToast(err?.message || 'Failed to send reward', 'error'); }
    finally { setSubmitting(false); }
  };

  const filteredEmps = employees.filter(e => {
    const q = empSearch.toLowerCase();
    return !q || `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) || (e.employeeId || '').toLowerCase().includes(q);
  });

  const selectedEmp = employees.find(e => e._id === rewardForm.receiverId);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>{toast.msg}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Trophy className="w-6 h-6 text-yellow-500"/>My Rewards</h1>
          <p className="text-sm text-gray-500 mt-1">Your recognition, peer rewards and leaderboard</p>
        </div>
        <button onClick={() => { setShowRewardForm(true); setEmpSearch(''); setRewardForm({ receiverId:'', rating:0, category:'teamwork', comment:'' }); }} className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600">
          <Gift className="w-4 h-4"/>Reward a Colleague
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-yellow-50 rounded-xl p-4 text-center border border-yellow-200">
          <p className="text-2xl font-bold text-yellow-700">{data.rewards?.length || 0}</p>
          <p className="text-xs text-gray-500 mt-0.5">Admin Awards</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 text-center border border-purple-200">
          <p className="text-2xl font-bold text-purple-700">{peerData.totalCount}</p>
          <p className="text-xs text-gray-500 mt-0.5">Peer Rewards</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-200">
          <p className="text-2xl font-bold text-blue-700">{(data.totalPoints || 0) + (peerData.totalPoints || 0)}</p>
          <p className="text-xs text-gray-500 mt-0.5">Total Points</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center border border-green-200">
          <p className="text-2xl font-bold text-green-700">{givenData.remainingThisMonth}</p>
          <p className="text-xs text-gray-500 mt-0.5">Rewards Left (Month)</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit flex-wrap">
        {[['mine','My Awards'],['peer','Peer Rewards'],['peer-leaderboard','Leaderboard'],['leaderboard','Admin Board']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab===k?'bg-white shadow text-yellow-600':'text-gray-600 hover:text-gray-900'}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="text-center py-12 text-gray-400">Loading...</div> : <>

        {/* My Admin Awards */}
        {tab === 'mine' && (
          data.rewards?.length === 0
            ? <div className="flex flex-col items-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200"><Trophy className="w-12 h-12 mb-3 opacity-30"/><p>No awards yet. Keep up the great work!</p></div>
            : <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.rewards?.map(r => (
                  <div key={r._id} className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border border-yellow-200 p-5">
                    <div className="flex items-center gap-2 mb-3"><Trophy className="w-6 h-6 text-yellow-500"/><span className="text-xs font-semibold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">{CAT_LABELS[r.category] || r.category}</span></div>
                    <p className="font-bold text-gray-900">{r.title}</p>
                    {r.description && <p className="text-sm text-gray-600 mt-1">{r.description}</p>}
                    <div className="flex gap-3 mt-3">
                      {r.points > 0 && <div className="flex items-center gap-1 text-yellow-700 text-sm font-semibold"><Star className="w-4 h-4"/>{r.points} pts</div>}
                      {r.cashAmount > 0 && <div className="flex items-center gap-1 text-green-700 text-sm font-semibold"><IndianRupee className="w-4 h-4"/>₹{r.cashAmount}</div>}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">{MONTH_NAMES[(r.month||1)-1]} {r.year} · By {r.nominatedBy?.firstName}</p>
                  </div>
                ))}
              </div>
        )}

        {/* Peer Rewards Received */}
        {tab === 'peer' && (
          peerData.rewards?.length === 0
            ? <div className="flex flex-col items-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200"><Gift className="w-12 h-12 mb-3 opacity-30"/><p>No peer rewards received yet.</p></div>
            : <div className="space-y-3">
                {peerData.rewards?.map(r => (
                  <div key={r._id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0 ${r.rating >= 4 ? 'bg-green-100 text-green-700' : r.rating >= 3 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                      {r.rating}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <p className="font-semibold text-gray-900">{PEER_CAT_LABELS[r.category] || r.category}</p>
                        <span className="text-xs text-purple-700 font-bold">+{r.points} pts</span>
                      </div>
                      {r.comment && <p className="text-sm text-gray-600 italic">"{r.comment}"</p>}
                      <p className="text-xs text-gray-400 mt-1">From {r.giver?.firstName} {r.giver?.lastName} · {r.quarter} {r.year}</p>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= r.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`}/>)}
                    </div>
                  </div>
                ))}
              </div>
        )}

        {/* Peer Leaderboard */}
        {tab === 'peer-leaderboard' && (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-white rounded-xl border border-gray-200">
              <div className="flex gap-1">
                {PERIOD_TYPES.map(p => (
                  <button key={p.v} onClick={() => { setLbPeriodType(p.v); setLbPeriod(p.v === 'monthly' ? String(new Date().getMonth()+1) : p.v === 'quarterly' ? 'Q1' : ''); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${lbPeriodType===p.v?'bg-blue-600 text-white':'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{p.l}</button>
                ))}
              </div>
              {lbPeriodType === 'monthly' && (
                <select value={lbPeriod} onChange={e => setLbPeriod(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs">
                  {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              )}
              {lbPeriodType === 'quarterly' && (
                <select value={lbPeriod} onChange={e => setLbPeriod(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs">
                  {QUARTERS.map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-100 font-semibold text-gray-800 flex items-center gap-2"><Users className="w-4 h-4 text-blue-500"/>Peer Reward Leaderboard</div>
              {peerLeaderboard.length === 0
                ? <div className="py-12 text-center text-gray-400">No data for this period</div>
                : <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>{['Rank','Employee','Dept','Rewards','Points'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {peerLeaderboard.map((e) => (
                        <tr key={e._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-bold ${e.rank===1?'bg-yellow-400 text-white':e.rank===2?'bg-gray-300 text-gray-800':e.rank===3?'bg-orange-300 text-white':'bg-gray-100 text-gray-700'}`}>{e.rank}</span>
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">{e.employee?.firstName} {e.employee?.lastName}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{e.employee?.department || '—'}</td>
                          <td className="px-4 py-3 text-gray-600 font-semibold">{e.totalRewards}</td>
                          <td className="px-4 py-3 font-bold text-purple-700">{e.totalPoints}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </div>
          </div>
        )}

        {/* Admin Leaderboard */}
        {tab === 'leaderboard' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 font-semibold text-gray-800">🏆 Admin Awards Leaderboard — {YEAR}</div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>{['Rank','Employee','Awards','Points'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {leaderboard.map((e, i) => (
                  <tr key={e._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3"><span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-bold ${i===0?'bg-yellow-400 text-white':i===1?'bg-gray-300 text-gray-800':i===2?'bg-orange-300 text-white':'bg-gray-100 text-gray-700'}`}>{i+1}</span></td>
                    <td className="px-4 py-3 font-medium text-gray-900">{e.employee?.firstName} {e.employee?.lastName}</td>
                    <td className="px-4 py-3 text-gray-600 font-semibold">{e.awardCount}</td>
                    <td className="px-4 py-3 text-yellow-700 font-bold">{e.totalPoints}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>}

      {/* Give Peer Reward Modal */}
      {showRewardForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Gift className="w-5 h-5 text-yellow-500"/>Reward a Colleague</h2>
              <button onClick={() => setShowRewardForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-blue-50 rounded-lg px-3 py-2 text-xs text-blue-700">
                Rewards remaining this month: <strong>{givenData.remainingThisMonth} / {givenData.maxPerMonth}</strong>
              </div>

              {/* Employee picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee *</label>
                <input value={empSearch} onChange={e => { setEmpSearch(e.target.value); setRewardForm(f => ({ ...f, receiverId: '' })); }} placeholder="Search by name or ID..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-1"/>
                <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredEmps.length === 0
                    ? <p className="px-3 py-2 text-sm text-gray-400">No employees found</p>
                    : filteredEmps.map(e => (
                        <button key={e._id} type="button" onClick={() => { setRewardForm(f => ({ ...f, receiverId: e._id })); setEmpSearch(`${e.firstName} ${e.lastName} (${e.employeeId})`); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-yellow-50 flex justify-between items-center ${rewardForm.receiverId === e._id ? 'bg-yellow-50 font-medium' : ''}`}>
                          <span>{e.firstName} {e.lastName}</span>
                          <span className="text-xs text-gray-400">{e.employeeId}</span>
                        </button>
                      ))
                  }
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating *</label>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(r => (
                    <button key={r} type="button" onClick={() => setRewardForm(f => ({ ...f, rating: r }))} className={`flex-1 flex flex-col items-center p-2 rounded-xl border-2 transition-all ${rewardForm.rating===r?'border-yellow-500 bg-yellow-50':'border-gray-200 hover:border-yellow-200'}`}>
                      <Star className={`w-5 h-5 ${r <= rewardForm.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}/>
                      <span className="text-xs text-gray-500 mt-0.5">{r}</span>
                      <span className="text-xs text-purple-600 font-semibold">+{RATING_POINTS[r]}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={rewardForm.category} onChange={e => setRewardForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  {Object.entries(PEER_CAT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Comment (optional)</label>
                <textarea value={rewardForm.comment} onChange={e => setRewardForm(f => ({ ...f, comment: e.target.value }))} rows={2} maxLength={500} placeholder="Why are you recognizing them?" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/>
              </div>

              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowRewardForm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={handleGivePeerReward} disabled={submitting || !rewardForm.receiverId || !rewardForm.rating} className="flex-1 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 disabled:opacity-60 flex items-center justify-center gap-2">
                  <Send className="w-4 h-4"/>{submitting ? 'Sending...' : 'Send Reward'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRewardsPage;
